import { BibleVersion } from '../../../models';
import { CHAPTER_VERSE_COUNTS, BOOK_ABBREV } from '../../../lib/bible-config';

interface PassageReference {
  book: string;
  chapter: number;
  startVerse: number;
  endVerse: number;
}

type ApiVerse = {
  book_id: string;
  book_name: string;
  chapter: number;
  verse: number;
  text: string;
};

export type BibleApiResponse = {
  reference: string;
  verses: ApiVerse[];
  text: string;
  translation_id: string;
  translation_name: string;
  translation_note: string;
};

/**
 * Parses an array of passage data into an array of Bible verse reference strings
 * for an API that accepts one book at a time. It groups passages by book and
 * merges contiguous chapter ranges.
 *
 * @param rawPassages - An array of passage objects.
 * @returns An array of formatted verse reference strings (e.g., ["Genesis+1:1-34:23"]).
 */
/**
 * Parses a reading plan reference into properly formatted API strings.
 * Handles references like "Gen 1-Gen 3:8" and splits multi-book references.
 *
 * @param reference - A reading plan reference string
 * @returns Array of properly formatted API reference strings
 */
export function parseReadingPlanReference(reference: string): string[] {
  // Remove extra spaces and trim
  const cleanRef = reference.trim().replace(/\s+/g, ' ');

  // Handle different reference patterns
  const patterns = {
    // Pattern: "Book StartChapter:StartVerse-Book EndChapter:EndVerse" (e.g., "Gen 3:9-Gen 5:22")
    crossChapterWithVerses:
      /^([A-Za-z0-9\s]+)\s+(\d+):(\d+)-([A-Za-z0-9\s]+)\s+(\d+):(\d+)$/,
    // Pattern: "Book StartChapter:StartVerse-Book EndChapter" (e.g., "Gen 48:21-Gen 50")
    crossChapterPartial:
      /^([A-Za-z0-9\s]+)\s+(\d+):(\d+)-([A-Za-z0-9\s]+)\s+(\d+)$/,
    // Pattern: "Book StartChapter-EndChapter:EndVerse" (e.g., "Gen 1-Gen 3:8")
    crossChapter: /^([A-Za-z0-9\s]+)\s+(\d+)-([A-Za-z0-9\s]+)\s+(\d+):(\d+)$/,
    // Pattern: "Book StartVerse-Book EndChapter, NextBook Start-End" (e.g., "Gen 48:21-Gen 50, Exod 1:1-3")
    multiBook:
      /^([A-Za-z0-9\s]+)\s+(\d+):(\d+)-([A-Za-z0-9\s]+)\s+(\d+),\s*([A-Za-z0-9\s]+)\s+(\d+):(\d+)-(\d+)$/,
    // Pattern: "Book Chapter:Start-End" (e.g., "Exod 1:1-3")
    singleBook: /^([A-Za-z0-9\s]+)\s+(\d+):(\d+)-(\d+)$/,
  };

  const apiStrings: string[] = [];

  // Try cross-chapter with verses pattern first (e.g., "Gen 3:9-Gen 5:22")
  const crossChapterWithVersesMatch = cleanRef.match(
    patterns.crossChapterWithVerses
  );
  if (crossChapterWithVersesMatch) {
    const [, startBook, startChapter, startVerse, , endChapter, endVerse] =
      crossChapterWithVersesMatch;

    // Find the book key from the abbreviation
    const bookKey = Object.keys(BOOK_ABBREV).find(
      (key) => BOOK_ABBREV[key] === startBook
    );

    if (!bookKey || !CHAPTER_VERSE_COUNTS[bookKey]) {
      console.error(`Unknown book: ${startBook}`);
      return [cleanRef]; // Return original as fallback
    }

    // Build the range: startChapter:startVerse to endChapter:endVerse
    const apiRef = `${bookKey} ${startChapter}:${startVerse}-${endChapter}:${endVerse}`;
    apiStrings.push(apiRef);
  }

  // Try cross-chapter partial pattern (e.g., "Gen 48:21-Gen 50")
  const crossChapterPartialMatch = cleanRef.match(patterns.crossChapterPartial);
  if (crossChapterPartialMatch && apiStrings.length === 0) {
    const [, startBook, startChapter, startVerse, , endChapter] =
      crossChapterPartialMatch;

    // Find the book key from the abbreviation
    const bookKey = Object.keys(BOOK_ABBREV).find(
      (key) => BOOK_ABBREV[key] === startBook
    );

    if (!bookKey || !CHAPTER_VERSE_COUNTS[bookKey]) {
      console.error(`Unknown book: ${startBook}`);
      return [cleanRef]; // Return original as fallback
    }

    const verseCounts = CHAPTER_VERSE_COUNTS[bookKey];
    const endChapNum = parseInt(endChapter) - 1; // 0-indexed

    if (endChapNum < 0 || endChapNum >= verseCounts.length) {
      console.error(`Invalid chapter: ${endChapter}`);
      return [cleanRef];
    }

    // Get the last verse of the end chapter
    const lastVerse = verseCounts[endChapNum];

    // Build the range: startChapter:startVerse to endChapter:lastVerse
    const apiRef = `${bookKey} ${startChapter}:${startVerse}-${endChapter}:${lastVerse}`;
    apiStrings.push(apiRef);
  }

  // Try cross-chapter pattern (e.g., "Gen 1-Gen 3:8")
  const crossChapterMatch = cleanRef.match(patterns.crossChapter);
  if (crossChapterMatch && apiStrings.length === 0) {
    const [, startBook, startChapter, , endChapter, endVerse] =
      crossChapterMatch;

    // Find the book key from the abbreviation
    const bookKey = Object.keys(BOOK_ABBREV).find(
      (key) => BOOK_ABBREV[key] === startBook
    );

    if (!bookKey || !CHAPTER_VERSE_COUNTS[bookKey]) {
      console.error(`Unknown book: ${startBook}`);
      return [cleanRef]; // Return original as fallback
    }

    const verseCounts = CHAPTER_VERSE_COUNTS[bookKey];
    const startChap = parseInt(startChapter) - 1; // 0-indexed
    const endChap = parseInt(endChapter) - 1; // 0-indexed

    if (startChap < 0 || endChap >= verseCounts.length) {
      console.error(`Invalid chapter range: ${startChapter}-${endChapter}`);
      return [cleanRef];
    }

    // Build the range: startChapter:1 to endChapter:endVerse
    const apiRef = `${bookKey} ${startChapter}:1-${endChapter}:${endVerse}`;
    apiStrings.push(apiRef);
  }

  // Try multi-book pattern
  const multiBookMatch = cleanRef.match(patterns.multiBook);
  if (multiBookMatch && apiStrings.length === 0) {
    const [
      ,
      firstBook,
      firstChap,
      firstVerse,
      ,
      lastChap,
      secondBook,
      secondStart,
      secondEnd,
    ] = multiBookMatch;

    // Handle first book range
    const firstBookKey = Object.keys(BOOK_ABBREV).find(
      (key) => BOOK_ABBREV[key] === firstBook
    );

    if (firstBookKey && CHAPTER_VERSE_COUNTS[firstBookKey]) {
      const verseCounts = CHAPTER_VERSE_COUNTS[firstBookKey];
      const lastChapNum = parseInt(lastChap) - 1;

      if (lastChapNum < verseCounts.length) {
        const lastVerse = verseCounts[lastChapNum];
        apiStrings.push(
          `${firstBookKey} ${firstChap}:${firstVerse}-${lastChap}:${lastVerse}`
        );
      }
    }

    // Handle second book range
    const secondBookKey = Object.keys(BOOK_ABBREV).find(
      (key) => BOOK_ABBREV[key] === secondBook
    );

    if (secondBookKey) {
      apiStrings.push(
        `${secondBookKey} ${secondStart}:${secondStart}-${secondEnd}`
      );
    }
  }

  // Try single book pattern
  const singleBookMatch = cleanRef.match(patterns.singleBook);
  if (singleBookMatch && apiStrings.length === 0) {
    const [, book, chapter, startVerse, endVerse] = singleBookMatch;

    const bookKey = Object.keys(BOOK_ABBREV).find(
      (key) => BOOK_ABBREV[key] === book
    );

    if (bookKey) {
      apiStrings.push(`${bookKey} ${chapter}:${startVerse}-${endVerse}`);
    }
  }

  // If no pattern matched, return the original reference as fallback
  if (apiStrings.length === 0) {
    console.warn(`Could not parse reference: ${cleanRef}`);
    apiStrings.push(cleanRef.toLowerCase());
  }

  return apiStrings;
}

export function parsePassagesToApiStrings(
  rawPassages: PassageReference[]
): string[] {
  if (!rawPassages || rawPassages.length === 0) {
    return [];
  }

  // 1. Group passages by book
  const passagesByBook = rawPassages.reduce((acc, passage) => {
    if (!acc[passage.book]) {
      acc[passage.book] = [];
    }
    acc[passage.book].push(passage);
    return acc;
  }, {} as Record<string, PassageReference[]>);

  const apiStrings: string[] = [];

  // 2. Process each book's passages
  for (const book in passagesByBook) {
    const passages = passagesByBook[book];

    // Sort passages by chapter to handle out-of-order data
    passages.sort(
      (a: PassageReference, b: PassageReference) => a.chapter - b.chapter
    );

    let currentRangeStart = passages[0];

    for (let i = 1; i < passages.length; i++) {
      const prevPassage = passages[i - 1];
      const currentPassage = passages[i];

      // 3. Check if chapters are contiguous
      if (currentPassage.chapter !== prevPassage.chapter + 1) {
        // If not contiguous, finalize the previous range and start a new one
        apiStrings.push(
          `${book.replace(/ /g, '+')}+${currentRangeStart.chapter}:${
            currentRangeStart.startVerse
          }-${prevPassage.chapter}:${prevPassage.endVerse}`
        );
        currentRangeStart = currentPassage;
      }
    }

    // 4. Add the last (or only) range for the current book
    const lastPassageInRange = passages[passages.length - 1];
    if (currentRangeStart.chapter === lastPassageInRange.chapter) {
      // Single chapter range
      apiStrings.push(
        `${book.replace(/ /g, '+')}+${currentRangeStart.chapter}:${
          currentRangeStart.startVerse
        }-${currentRangeStart.endVerse}`
      );
    } else {
      // Multi-chapter range
      apiStrings.push(
        `${book.replace(/ /g, '+')}+${currentRangeStart.chapter}:${
          currentRangeStart.startVerse
        }-${lastPassageInRange.chapter}:${lastPassageInRange.endVerse}`
      );
    }
  }

  return apiStrings;
}

export const fetchFromBibleApi = async (
  verseReference: string,
  translation: BibleVersion
) => {
  try {
    // Parse the reading plan reference into properly formatted API strings
    const apiReferences = parseReadingPlanReference(verseReference);

    // Fetch all parsed references in parallel
    const fetchPromises = apiReferences.map(async (apiRef) => {
      // Format the verse reference for the API request
      const formattedRef = apiRef
        .replace(/\s+/g, '+') // Replace spaces with '+'
        .replace(/[\u2013\u2014]/g, '-'); // Replace en/em dash with hyphen

      let apiUrl = `https://bible-api.com/${formattedRef}?translation=${translation}`;

      // Handle single-chapter books to fetch the whole chapter instead of a single verse
      const singleChapterPattern = /^([a-zA-Z0-9\s]+)\s\d+$/;
      const match = apiRef.toLowerCase().match(singleChapterPattern);
      const SINGLE_CHAPTER_BOOKS = [
        'obadiah',
        'philemon',
        '2 john',
        '3 john',
        'jude',
      ];
      if (match && SINGLE_CHAPTER_BOOKS.includes(match[1])) {
        apiUrl += `&single_chapter_book_matching=indifferent`;
      }

      // Fetch the verse content from the Bible API
      const response = await fetch(apiUrl);

      // Handle API errors (e.g., "verse not found")
      if (!response.ok) {
        let errorMessage = `Bible API error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData && errorData.error) {
            errorMessage += ` - ${errorData.error}`;
          }
        } catch (e) {
          /* Response body was not JSON */
        }
        console.error(errorMessage, `for reference: ${apiRef}`);
        return { content: [], reference: apiRef };
      }

      const data: BibleApiResponse = await response.json();

      return {
        content: data.verses.map((v) => ({
          // Map to the local Verse type, ensuring consistency
          book_name: v.book_name,
          chapter: v.chapter,
          verse: v.verse,
          text: v.text,
        })),
        reference: data.reference || apiRef,
      };
    });

    // Wait for all API calls to complete
    const results = await Promise.all(fetchPromises);

    // Combine all results
    const combinedContent = results.flatMap((result) => result.content);
    const combinedReference = results
      .map((result) => result.reference)
      .join('; ');

    return {
      content: combinedContent,
      reference: combinedReference || verseReference,
    };
  } catch (error) {
    console.error(`Error fetching ${translation} verse:`, error);
    // On network or other unexpected errors, return empty content
    return { content: [], reference: verseReference };
  }
};
